import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, PiggyBank, Plus, Users } from 'lucide-react'
import { Fragment, useState, type ReactNode } from 'react'

import type {
  ExpenseEntry,
  ExpenseSort,
  ExpensesRequest,
  PayrollRow,
} from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney, isPositive } from '@aeris/money'
import {
  expenseSourceLabel as sourceLabel,
  payrollStatusText,
} from '@aeris/status-meta'
import {
  ActiveFilters,
  Badge,
  Button,
  EmptyState,
  Field,
  InlineAlert,
  ListToolbar,
  LoadFailed,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  SearchInput,
  Select,
  SortableHeaderCell,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  VisuallyHidden,
  cn,
  type ActiveFilterChip,
} from '@aeris/ui'

import { EmployeesDialog } from '../components/employees-dialog.tsx'
import {
  ExpenseEntryDialog,
  type CashUnavailable,
  type ExpenseDialogMode,
} from '../components/expense-entry-dialog.tsx'
import { ExpenseVoidDialog } from '../components/expense-void-dialog.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import { PayrollAdjustmentDialog } from '../components/payroll-adjustment-dialog.tsx'
import { fetchCurrentCashSession } from '../lib/api/cash.ts'
import { periodLabel, periodOf, shiftPeriod } from '../lib/api/commissions.ts'
import {
  fetchExpenseCategories,
  fetchExpenses,
  fetchPayroll,
} from '../lib/api/expenses.ts'
import { useAuth } from '../lib/auth-context.ts'
import { EXPENSE_EXPORT } from '../lib/expense-export.ts'
import { EARNING_LABELS } from '../lib/payroll-labels.ts'
import { readRememberedTerminal } from '../lib/remembered-terminal.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { useTableSort } from '../lib/use-table-sort.ts'

/**
 * Folha e gastos (F146).
 *
 * *"ele vai colocar os funcionários e colocar cada valor x que quem recebe, e
 * a menina vai preenchendo quando pagar; e também ter uma só de gasto —
 * comprei 10 reais de marmita pro Eduardo, comprei bobina pra loja — aí no
 * final ele sabe o que gastou"*.
 *
 * Três abas sobre o mesmo mês: a folha (quanto falta pagar a cada um), o
 * caderno (tudo o que saiu) e o resumo (por categoria). Uma tela só para o
 * Site e o Modo PC.
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

/**
 * Vinte e cinco por página, como toda lista do sistema.
 *
 * Até a F187 a aba pedia o mês inteiro com um teto silencioso de 2 000 — e o
 * resumo saía da soma das linhas devolvidas, então um mês que passasse disso
 * daria um "Total do mês" errado sem nada na tela dizendo.
 */
const PAGE_SIZE = 25

/** `2026-09-14` → `14/09`. */
const dayLabel = (spentOn: string): string => {
  const [, month = '', day = ''] = spentOn.split('-')
  return `${day}/${month}`
}

/**
 * "Salário 2.000,00 + Bônus fixo 500,00 + Meta 300,00 − Faltou 120,00" — F157.
 *
 * `null` quando não há o que decompor: uma pessoa que só tem salário leria o
 * mesmo número duas vezes, uma delas com uma palavra na frente.
 */
const composition = (row: PayrollRow): string | null => {
  const partes = row.earnings.length + row.adjustments.length
  if (partes < 2) return null

  const ganhos = row.earnings.map(
    (earning) => `${EARNING_LABELS[earning.kind]} ${money(earning.amount)}`
  )
  const bonus = row.adjustments
    .filter((adjustment) => adjustment.kind === 'bonus')
    .map(
      (adjustment) => `${adjustment.description} ${money(adjustment.amount)}`
    )
  // O desconto entra com o sinal, porque o valor gravado é sempre positivo.
  const descontos = row.adjustments
    .filter((adjustment) => adjustment.kind === 'discount')
    .map(
      (adjustment) => `− ${adjustment.description} ${money(adjustment.amount)}`
    )

  return (
    [...ganhos, ...bonus].join(' + ') +
    (descontos.length === 0 ? '' : ` ${descontos.join(' ')}`)
  )
}

/** F186: "Pago a mais" não é verde — ver `payrollStatusText`. */
const PayrollBadge = ({ row }: { row: PayrollRow }): ReactNode => {
  const text = payrollStatusText(row.status)
  return <Badge tone={text.tone}>{text.label}</Badge>
}

export const GastosPage = (): ReactNode => {
  const { allows, session: authSession } = useAuth()
  const canWrite = allows('expense.write')
  const canManage = allows('expense.manage')
  /*
   * A folha tem sigilo próprio desde a F157: o caderno de gastos continua de
   * quem sempre foi, e quanto cada um ganha fica com o dono e o admin. Sem
   * isto, a aba inteira some — e é assim que deve ser: uma aba vazia dizendo
   * "você não pode ver" é o salário dito em voz alta de outro jeito.
   */
  const canSeePayroll = allows('payroll.read')
  const canManagePayroll = allows('payroll.manage')

  const currentPeriod = periodOf(new Date())
  const [period, setPeriod] = useState(currentPeriod)
  const [tab, setTab] = useState(canSeePayroll ? 'folha' : 'gastos')

  /*
   * O recorte do caderno — F187. Até aqui a aba só tinha o mês: com 3 a 6
   * lançamentos por dia, o mês fecha com 100 a 180 linhas, e "quanto eu gastei
   * com marmita?" se respondia rolando a tela com o olho.
   */
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [employeeId, setEmployeeId] = useState('')
  const [paymentSource, setPaymentSource] = useState('')
  const [entryStatus, setEntryStatus] = useState('')
  const [page, setPage] = useState(1)
  const entrySort = useTableSort<ExpenseSort>('spentOn', 'desc', () => {
    setPage(1)
  })
  const debouncedSearch = useDebounced(search)
  const [dialog, setDialog] = useState<ExpenseDialogMode | null>(null)
  const [managing, setManaging] = useState(false)
  const [voiding, setVoiding] = useState<ExpenseEntry | null>(null)
  const [openRow, setOpenRow] = useState<string | null>(null)
  const [adjusting, setAdjusting] = useState<PayrollRow | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const payroll = useQuery({
    queryKey: ['expenses', 'payroll', period],
    queryFn: ({ signal }) => fetchPayroll(period, signal),
    // Sem permissão, nem se pergunta: o servidor responderia 403.
    enabled: canSeePayroll,
  })
  const listRequest: ExpensesRequest = {
    period,
    ...(debouncedSearch.trim() === ''
      ? {}
      : { search: debouncedSearch.trim() }),
    ...(categoryId === '' ? {} : { categoryId }),
    ...(employeeId === '' ? {} : { employeeId }),
    ...(paymentSource === ''
      ? {}
      : { paymentSource: paymentSource as 'cash' | 'other' }),
    ...(entryStatus === ''
      ? {}
      : { status: entryStatus as 'active' | 'voided' }),
    page,
    pageSize: PAGE_SIZE,
    sort: entrySort.sort,
    direction: entrySort.direction,
  }

  const month = useQuery({
    queryKey: ['expenses', 'entries', listRequest],
    queryFn: ({ signal }) => fetchExpenses(listRequest, signal),
    /*
     * Mantém a página anterior enquanto a próxima chega: sem isto, cada letra
     * digitada na busca troca a tabela por um spinner, e a lista "pisca" a
     * cada clique na seta do mês. É o que o Estoque já faz.
     */
    placeholderData: (previous) => previous,
  })

  /*
   * As categorias alimentam o filtro, e a lista delas é de quem lança gasto —
   * `expense.read`, a mesma da aba. O diálogo de lançar já as pedia; aqui elas
   * sobem para a tela porque o filtro é da lista, não do lançamento.
   */
  const categoryQuery = useQuery({
    queryKey: ['expense-categories'],
    queryFn: ({ signal }) => fetchExpenseCategories(signal),
  })
  const categories = (categoryQuery.data?.categories ?? []).filter(
    (category) => category.status === 'active'
  )

  /*
   * O caixa aberto deste terminal, o mesmo que o Financeiro usa para a baixa
   * em dinheiro: "dinheiro do caixa" sai desta gaveta.
   */
  const terminalId = readRememberedTerminal()
  const branchId = authSession?.context?.branchId ?? undefined
  const till = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession({ terminalId, branchId }, signal),
    enabled: terminalId !== '',
  })
  const cashSessionId = till.data?.session?.id ?? null
  const cashUnavailable: CashUnavailable =
    terminalId === ''
      ? 'no-terminal'
      : cashSessionId === null
        ? 'no-session'
        : null

  const rows = payroll.data?.rows ?? []
  const entries = month.data?.entries ?? []
  const summary = month.data?.summary

  /*
   * F186: os dois lados do mês, cada um com a sua cor. "Falta" fica âmbar
   * enquanto houver alguém por receber e volta ao preto quando zera — é o
   * "pagou todo mundo" visível de longe. "Pago a mais" é vermelho porque
   * pede decisão de alguém.
   */
  /*
   * Os funcionários do filtro saem da própria folha, que esta tela já carrega
   * quando quem olha pode vê-la. Pedir a lista de `/employees` seria uma
   * segunda leitura do mesmo dado — e ela carrega salário.
   */
  const employeeOptions = rows.map((row) => ({
    id: row.employeeId,
    name: row.name,
  }))

  const filtering =
    debouncedSearch.trim() !== '' ||
    categoryId !== '' ||
    employeeId !== '' ||
    paymentSource !== '' ||
    entryStatus !== ''

  const clearFilters = (): void => {
    setSearch('')
    setCategoryId('')
    setEmployeeId('')
    setPaymentSource('')
    setEntryStatus('')
    setPage(1)
  }

  const chips: ActiveFilterChip[] = [
    ...(debouncedSearch.trim() === ''
      ? []
      : [
          {
            id: 'search',
            label: `Busca: ${debouncedSearch.trim()}`,
            onRemove: () => {
              setSearch('')
              setPage(1)
            },
          },
        ]),
    ...(categoryId === ''
      ? []
      : [
          {
            id: 'category',
            label: `Categoria: ${categories.find((row) => row.id === categoryId)?.name ?? '—'}`,
            onRemove: () => {
              setCategoryId('')
              setPage(1)
            },
          },
        ]),
    ...(employeeId === ''
      ? []
      : [
          {
            id: 'employee',
            label: `Funcionário: ${employeeOptions.find((row) => row.id === employeeId)?.name ?? '—'}`,
            onRemove: () => {
              setEmployeeId('')
              setPage(1)
            },
          },
        ]),
    ...(paymentSource === ''
      ? []
      : [
          {
            id: 'source',
            label: `Pago com: ${paymentSource === 'cash' ? 'Caixa' : 'Pix/banco/outro'}`,
            onRemove: () => {
              setPaymentSource('')
              setPage(1)
            },
          },
        ]),
    ...(entryStatus === ''
      ? []
      : [
          {
            id: 'status',
            label: entryStatus === 'active' ? 'Só o que vale' : 'Só o desfeito',
            onRemove: () => {
              setEntryStatus('')
              setPage(1)
            },
          },
        ]),
  ]

  const owingPayroll = isPositive(
    parseAmount(payroll.data?.totals.remainingAmount ?? '0.00')
  )
  const overpaidPayroll = isPositive(
    parseAmount(payroll.data?.totals.overpaidAmount ?? '0.00')
  )

  const voidAction = (entry: ExpenseEntry): ReactNode =>
    canManage && entry.status === 'active' ? (
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Desfazer ${entry.description}`}
        onClick={() => {
          setVoiding(entry)
        }}
      >
        Desfazer
      </Button>
    ) : null

  return (
    <Page>
      <PageHeader
        title="Folha e gastos"
        description="A folha dos funcionários no mês e tudo o que a loja gastou."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Mês anterior"
                leadingIcon={
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setPeriod((value) => shiftPeriod(value, -1))
                }}
              />
              <span
                aria-live="polite"
                className="min-w-40 text-center text-(length:--text-sm) font-(--weight-semibold) capitalize"
              >
                {periodLabel(period)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Próximo mês"
                disabled={period >= currentPeriod}
                leadingIcon={
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setPeriod((value) => shiftPeriod(value, 1))
                }}
              />
            </div>
            {/* Quem define quanto cada um ganha é folha, não caderno (F157). */}
            {canManagePayroll ? (
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Users aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  setManaging(true)
                }}
              >
                Funcionários
              </Button>
            ) : null}
            {canWrite ? (
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  setDialog({ kind: 'expense' })
                }}
              >
                Lançar gasto
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          label="Total do mês"
          loading={month.isPending}
          error={month.isError}
          value={summary === undefined ? '—' : money(summary.totalAmount)}
        />
        <StatTile
          label="Folha paga"
          loading={month.isPending}
          error={month.isError}
          value={summary === undefined ? '—' : money(summary.payrollAmount)}
        />
        <StatTile
          label="Gastos"
          loading={month.isPending}
          error={month.isError}
          value={summary === undefined ? '—' : money(summary.expensesAmount)}
        />
        {/*
          F186. Falta e pago-a-mais são DOIS cards quando o segundo existe,
          nunca uma subtração: somados com sinal, um mês com o Dudu a receber
          R$ 500,00 e a Thais paga R$ 500,00 a mais escrevia "R$ 0,00", e o mês
          fechava com uma pessoa sem receber. O segundo card só aparece no mês
          em que há o que explicar — no mês normal ele seria um zero a mais
          para ler.
        */}
        <StatTile
          label="Falta pagar da folha"
          loading={payroll.isPending}
          error={payroll.isError}
          value={
            payroll.data === undefined ? (
              '—'
            ) : (
              <span className={owingPayroll ? 'text-(--warning)' : undefined}>
                {money(payroll.data.totals.remainingAmount)}
              </span>
            )
          }
        />
        {overpaidPayroll ? (
          <StatTile
            label="Pago a mais"
            value={
              <span className="text-(--danger)">
                {money(payroll.data?.totals.overpaidAmount ?? '0.00')}
              </span>
            }
            hint="Saiu mais do que a loja devia. Acerte no mês que vem."
          />
        ) : null}
      </div>

      {notice === null ? null : (
        <InlineAlert tone="warning" title="O dinheiro não voltou para a gaveta">
          {notice}
        </InlineAlert>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Folha e gastos">
          {canSeePayroll ? (
            <TabsTrigger value="folha">Folha</TabsTrigger>
          ) : null}
          <TabsTrigger value="gastos">Gastos</TabsTrigger>
          <TabsTrigger value="resumo">Resumo</TabsTrigger>
        </TabsList>

        {canSeePayroll ? (
          <TabsContent value="folha" className="mt-3">
            {payroll.isError ? (
              <LoadFailed
                what="a folha"
                onRetry={() => {
                  void payroll.refetch()
                }}
              />
            ) : payroll.isPending ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<Users aria-hidden="true" className="h-8 w-8" />}
                title="Ninguém na folha deste mês"
                description={
                  canManage
                    ? 'Cadastre os funcionários e o salário de cada um em Funcionários.'
                    : 'Quem cadastra os funcionários é o gerente.'
                }
              />
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Funcionário</TableHeaderCell>
                      {/*
                      "A receber", e não "Salário": desde a F157 a coluna soma
                      salário, bônus fixo, vales e os lançamentos do mês.
                    */}
                      <TableHeaderCell numeric>A receber</TableHeaderCell>
                      <TableHeaderCell numeric>Pago</TableHeaderCell>
                      <TableHeaderCell numeric>Falta</TableHeaderCell>
                      <TableHeaderCell>Situação</TableHeaderCell>
                      <TableHeaderCell>
                        <span className="sr-only">Ações</span>
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const expanded = openRow === row.employeeId
                      const owes =
                        row.remainingAmount !== null &&
                        isPositive(parseAmount(row.remainingAmount))
                      return (
                        <Fragment key={row.employeeId}>
                          <TableRow>
                            <TableCell>
                              <button
                                type="button"
                                aria-expanded={expanded}
                                className="flex flex-col text-left"
                                onClick={() => {
                                  setOpenRow(expanded ? null : row.employeeId)
                                }}
                              >
                                <span className="font-(--weight-medium) text-(--text-primary)">
                                  {row.name}
                                </span>
                                <span className="text-(length:--text-xs) text-(--text-muted)">
                                  {row.entries.length === 0
                                    ? 'nenhum pagamento no mês'
                                    : `${String(row.entries.length)} ${row.entries.length === 1 ? 'lançamento' : 'lançamentos'} · ver`}
                                </span>
                              </button>
                            </TableCell>
                            <TableCell numeric>
                              {row.dueAmount === null ? (
                                '—'
                              ) : (
                                <span className="flex flex-col items-end">
                                  {money(row.dueAmount)}
                                  {/*
                                  A composição, e só quando há mais de uma
                                  parcela: senão a coluna repetiria o número de
                                  cima com uma palavra na frente.
                                */}
                                  {composition(row) === null ? null : (
                                    <span className="text-(length:--text-xs) font-(--weight-normal) text-(--text-muted)">
                                      {composition(row)}
                                    </span>
                                  )}
                                </span>
                              )}
                            </TableCell>
                            <TableCell numeric>
                              {money(row.paidAmount)}
                            </TableCell>
                            <TableCell numeric>
                              {row.remainingAmount === null ? (
                                '—'
                              ) : (
                                <strong>{money(row.remainingAmount)}</strong>
                              )}
                            </TableCell>
                            <TableCell>
                              <PayrollBadge row={row} />
                            </TableCell>
                            <TableCell>
                              <span className="flex justify-end gap-1">
                                {/*
                                O bônus pontual e o desconto (F157). Fica ao
                                lado do Pagar porque é a mesma pergunta do mês:
                                quanto esta pessoa recebe. O recorrente é no
                                cadastro, em Funcionários.
                              */}
                                {canManagePayroll ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Bônus ou desconto de ${row.name}`}
                                    onClick={() => {
                                      setAdjusting(row)
                                    }}
                                  >
                                    Bônus/desconto
                                  </Button>
                                ) : null}
                                {canWrite && owes ? (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    aria-label={`Pagar ${row.name}`}
                                    onClick={() => {
                                      setDialog({ kind: 'salary', row })
                                    }}
                                  >
                                    Pagar
                                  </Button>
                                ) : null}
                              </span>
                            </TableCell>
                          </TableRow>
                          {expanded ? (
                            <TableRow>
                              {/* `td` do próprio HTML: a célula do kit não aceita `colSpan`. */}
                              <td
                                colSpan={6}
                                className="px-3 py-2 text-(--text-primary)"
                              >
                                {row.entries.length === 0 ? (
                                  <span className="text-(length:--text-sm) text-(--text-muted)">
                                    Nada saiu para {row.name} neste mês.
                                  </span>
                                ) : (
                                  <ul
                                    aria-label={`Pagamentos de ${row.name}`}
                                    className="flex flex-col gap-1 text-(length:--text-sm)"
                                  >
                                    {row.entries.map((entry) => (
                                      <li
                                        key={entry.id}
                                        className="flex flex-wrap items-center gap-2"
                                      >
                                        <span className="tabular-nums text-(--text-muted)">
                                          {dayLabel(entry.spentOn)}
                                        </span>
                                        <span>{entry.description}</span>
                                        <span className="text-(length:--text-xs) text-(--text-muted)">
                                          {sourceLabel(entry)}
                                        </span>
                                        <span className="ml-auto font-(--font-mono) tabular-nums">
                                          {money(entry.amount)}
                                        </span>
                                        {voidAction(entry)}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabsContent>
        ) : null}

        <TabsContent value="gastos" className="mt-3 flex flex-col gap-3">
          <ListToolbar
            actions={
              <ExportMenu
                fileName={`gastos-${period}`}
                sheetName="Gastos"
                columns={EXPENSE_EXPORT}
                /*
                 * O arquivo leva o RECORTE que está na tela, não o mês inteiro:
                 * quem filtrou "Alimentação" quer a planilha da alimentação. E
                 * ele pagina pela mesma rota da lista, então a permissão é a
                 * mesma — não há porta de exportação por onde saia mais do que
                 * a tela mostra.
                 */
                fetchPage={(exportPage) =>
                  fetchExpenses({
                    ...listRequest,
                    page: exportPage,
                    pageSize: 200,
                  }).then((response) => ({
                    items: response.entries,
                    total: response.total,
                  }))
                }
                pageSize={200}
                total={month.data?.total ?? 0}
              />
            }
          >
            <SearchInput
              aria-label="Buscar no caderno de gastos"
              placeholder="Buscar pelo que foi…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
            />
            {categories.length === 0 ? null : (
              <Field label="Categoria">
                {(field) => (
                  <Select
                    {...field}
                    value={categoryId}
                    onChange={(event) => {
                      setCategoryId(event.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            {/*
              O filtro por funcionário só existe para quem vê a folha: a lista
              de funcionários vem de `payroll.read`, e é a mesma separação da
              F157 — o caixa lança a marmita do Eduardo sem ver o que o Eduardo
              ganha.
            */}
            {canSeePayroll && employeeOptions.length > 0 ? (
              <Field label="Funcionário">
                {(field) => (
                  <Select
                    {...field}
                    value={employeeId}
                    onChange={(event) => {
                      setEmployeeId(event.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="">Todos</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : null}
            <Field label="Pago com">
              {(field) => (
                <Select
                  {...field}
                  value={paymentSource}
                  onChange={(event) => {
                    setPaymentSource(event.target.value)
                    setPage(1)
                  }}
                >
                  <option value="">De tudo</option>
                  <option value="cash">Caixa</option>
                  <option value="other">Pix/banco/outro</option>
                </Select>
              )}
            </Field>
            <Field label="Situação">
              {(field) => (
                <Select
                  {...field}
                  value={entryStatus}
                  onChange={(event) => {
                    setEntryStatus(event.target.value)
                    setPage(1)
                  }}
                >
                  {/*
                    O padrão mostra os dois, como sempre: o desfeito riscado é o
                    que conta a história do mês (F146). O filtro é para o dia em
                    que metade do caderno é estorno e alguém precisa ler só o
                    que valeu.
                  */}
                  <option value="">Vale e desfeito</option>
                  <option value="active">Só o que vale</option>
                  <option value="voided">Só o desfeito</option>
                </Select>
              )}
            </Field>
          </ListToolbar>

          <ActiveFilters chips={chips} onClearAll={clearFilters} />

          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <ResultCount
              page={month.data?.page ?? 1}
              pageSize={month.data?.pageSize ?? PAGE_SIZE}
              total={month.data?.total ?? 0}
              loading={month.isPending}
              none="Nenhum lançamento com esse filtro"
            />
            {/*
              A soma do RECORTE, que é a resposta de "quanto eu gastei com
              marmita?". O "Total do mês" lá em cima continua sendo o do mês
              inteiro de propósito — se ele obedecesse ao filtro, mudaria de
              significado sem mudar de nome.
            */}
            {month.data === undefined || !filtering ? null : (
              <p className="text-(length:--text-xs) text-(--text-secondary)">
                {`Soma do filtro: `}
                <strong className="font-(--font-mono) tabular-nums">
                  {money(month.data.filteredAmount)}
                </strong>
              </p>
            )}
          </div>

          <QueryState
            isPending={month.isPending}
            isError={month.isError}
            error={month.error}
            onRetry={() => {
              void month.refetch()
            }}
            what="os gastos"
            isEmpty={entries.length === 0}
            filtered={filtering}
            onClearFilters={clearFilters}
            empty={
              <EmptyState
                icon={<PiggyBank aria-hidden="true" className="h-8 w-8" />}
                title="Nenhum gasto neste mês"
                description="Tudo o que a loja pagar — marmita, bobina, salário — aparece aqui."
              />
            }
          >
            {/*
              No celular a tabela de seis colunas vira uma faixa que rola de
              lado, e "O que foi" — que carrega a descrição, quem lançou e o
              motivo do estorno — é justamente a que fica espremida. Cartões,
              no molde da tela de Equipe.
            */}
            <ul
              aria-label="Lançamentos do mês"
              className="flex flex-col gap-2 md:hidden"
            >
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-col gap-1 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface) p-3"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={cn(
                        'font-(--weight-medium)',
                        entry.status === 'voided' && 'line-through'
                      )}
                    >
                      {entry.description}
                    </span>
                    <span
                      className={cn(
                        'font-(--font-mono) tabular-nums whitespace-nowrap',
                        entry.status === 'voided' && 'line-through'
                      )}
                    >
                      {money(entry.amount)}
                    </span>
                  </div>
                  <p className="text-(length:--text-xs) text-(--text-muted)">
                    {[
                      dayLabel(entry.spentOn),
                      entry.kind === 'salary'
                        ? 'Folha'
                        : (entry.categoryName ?? 'Sem categoria'),
                      sourceLabel(entry),
                    ].join(' · ')}
                  </p>
                  {entry.status === 'voided' ? (
                    <p className="text-(length:--text-xs) text-(--danger)">
                      {`Desfeito${entry.voidedByName === null ? '' : ` por ${entry.voidedByName}`}: ${entry.voidReason ?? ''}`}
                    </p>
                  ) : null}
                  {voidAction(entry) === null ? null : (
                    <div className="flex justify-end">{voidAction(entry)}</div>
                  )}
                </li>
              ))}
            </ul>

            <TableContainer className="hidden md:block">
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableHeaderCell
                      kind="date"
                      {...entrySort.column('spentOn')}
                    >
                      Dia
                    </SortableHeaderCell>
                    <SortableHeaderCell {...entrySort.column('description')}>
                      O que foi
                    </SortableHeaderCell>
                    <SortableHeaderCell {...entrySort.column('category')}>
                      Categoria
                    </SortableHeaderCell>
                    <SortableHeaderCell {...entrySort.column('paymentSource')}>
                      Pago com
                    </SortableHeaderCell>
                    <SortableHeaderCell numeric {...entrySort.column('amount')}>
                      Valor
                    </SortableHeaderCell>
                    <TableHeaderCell>
                      <VisuallyHidden>Ações</VisuallyHidden>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => {
                    const voided = entry.status === 'voided'
                    return (
                      <TableRow key={entry.id}>
                        <TableCell className="tabular-nums">
                          {dayLabel(entry.spentOn)}
                        </TableCell>
                        <TableCell>
                          <span className="flex flex-col">
                            <span className={voided ? 'line-through' : ''}>
                              {entry.description}
                            </span>
                            <span className="text-(length:--text-xs) text-(--text-muted)">
                              {[
                                entry.employeeName === null
                                  ? null
                                  : entry.deductsSalary
                                    ? `${entry.employeeName} · desconta do salário`
                                    : `para ${entry.employeeName}`,
                                entry.actorName === null
                                  ? null
                                  : `lançado por ${entry.actorName}`,
                              ]
                                .filter((part) => part !== null)
                                .join(' · ')}
                            </span>
                            {voided ? (
                              <span className="text-(length:--text-xs) text-(--danger)">
                                {`Desfeito${entry.voidedByName === null ? '' : ` por ${entry.voidedByName}`}: ${entry.voidReason ?? ''}`}
                              </span>
                            ) : null}
                          </span>
                        </TableCell>
                        <TableCell>
                          {entry.kind === 'salary'
                            ? 'Folha'
                            : (entry.categoryName ?? '—')}
                        </TableCell>
                        <TableCell>{sourceLabel(entry)}</TableCell>
                        <TableCell numeric>
                          <span className={voided ? 'line-through' : ''}>
                            {money(entry.amount)}
                          </span>
                        </TableCell>
                        <TableCell>{voidAction(entry)}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </QueryState>

          <Pagination
            page={month.data?.page ?? 1}
            pageSize={month.data?.pageSize ?? PAGE_SIZE}
            total={month.data?.total ?? 0}
            onPageChange={setPage}
            label="Páginas do caderno"
          />
        </TabsContent>

        <TabsContent value="resumo" className="mt-3">
          {summary === undefined ? (
            month.isError ? (
              <LoadFailed
                what="o resumo"
                onRetry={() => {
                  void month.refetch()
                }}
              />
            ) : (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            )
          ) : (
            <dl
              aria-label={`Resumo de ${periodLabel(period)}`}
              className="flex max-w-lg flex-col divide-y divide-(--border-subtle) rounded-(--radius-md) border border-(--border-subtle) px-3 text-(length:--text-sm)"
            >
              {summary.byCategory.map((row) => (
                <div
                  key={row.categoryId}
                  className="flex items-center justify-between py-2"
                >
                  <dt>
                    {row.name}
                    <span className="ml-1 text-(length:--text-xs) text-(--text-muted)">
                      {`(${String(row.count)})`}
                    </span>
                  </dt>
                  <dd className="font-(--font-mono) tabular-nums">
                    {money(row.amount)}
                  </dd>
                </div>
              ))}
              <div className="flex items-center justify-between py-2">
                <dt>Folha</dt>
                <dd className="font-(--font-mono) tabular-nums">
                  {money(summary.payrollAmount)}
                </dd>
              </div>
              <div className="flex items-center justify-between py-2 font-(--weight-semibold)">
                <dt>Total do mês</dt>
                <dd className="font-(--font-mono) tabular-nums">
                  {money(summary.totalAmount)}
                </dd>
              </div>
              <div className="flex items-center justify-between py-2 text-(--text-muted)">
                <dt>Saiu do caixa</dt>
                <dd className="font-(--font-mono) tabular-nums">
                  {money(summary.cashAmount)}
                </dd>
              </div>
              <div className="flex items-center justify-between py-2 text-(--text-muted)">
                <dt>Pix, banco ou outro</dt>
                <dd className="font-(--font-mono) tabular-nums">
                  {money(summary.otherAmount)}
                </dd>
              </div>
            </dl>
          )}
        </TabsContent>
      </Tabs>

      {dialog === null ? null : (
        <ExpenseEntryDialog
          mode={dialog}
          period={period}
          payrollRows={rows}
          cashSessionId={cashSessionId}
          cashUnavailable={cashUnavailable}
          canManage={canManage}
          canSeePayroll={canSeePayroll}
          onClose={() => {
            setDialog(null)
          }}
        />
      )}

      {managing ? (
        <EmployeesDialog
          period={period}
          onClose={() => {
            setManaging(false)
          }}
        />
      ) : null}

      {adjusting === null ? null : (
        <PayrollAdjustmentDialog
          row={adjusting}
          period={period}
          onClose={() => {
            setAdjusting(null)
          }}
        />
      )}

      {voiding === null ? null : (
        <ExpenseVoidDialog
          entry={voiding}
          onClose={() => {
            setVoiding(null)
          }}
          onDone={({ cashReturned }) => {
            setNotice(
              voiding.paymentSource === 'cash' && !cashReturned
                ? `O caixa em que "${voiding.description}" saiu já fechou. O lançamento foi desfeito; se o dinheiro voltou para a gaveta, lance um suprimento no caixa atual.`
                : null
            )
            setVoiding(null)
          }}
        />
      )}
    </Page>
  )
}
